import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ArtistWebsiteService } from '../artist-website.service';
import { Section, isElementHidden } from '../../models/site-content.model';

/**
 * The minimum a caller must know about a piece to enquire about it.
 *
 * Deliberately structural rather than importing `SiteWork`: the website builder's document model is
 * one caller, and tying a shared service to it would mean anything else wanting to send an enquiry
 * had to construct a site document first. `SiteWork` satisfies this shape as-is, so the website
 * builder passes its works straight through with no mapping.
 */
export interface EnquiryArtwork {
  id: string;
  name: string;
  image?: string;
  /**
   * Whose work it is. Absent on a single-artist site, where the whole page answers that
   * question already; required on a multi-artist wall, where it is the first thing a visitor
   * needs to know.
   */
  artist?: string | null;
  /**
   * The artist's published-site slug, carried on the PIECE.
   *
   * The panel also receives an `identifier` input, but that arrives through a binding derived from
   * the work being opened — so during `openDetail()` it still holds the previous artist. Anything
   * that acts the moment a piece opens (prefetching their profile) must read it from the work
   * itself, not from the input, or it fetches the person whose piece was open before.
   */
  artistIdentifier?: string | null;
  series?: string | null;
  medium?: string | null;
  dims?: string | null;
  description?: string | null;
  price?: number | null;
  /**
   * Currency the price is quoted in. Optional because a single-artist site sets it once for the
   * whole page; on a multi-artist wall it varies per piece and belongs on the artwork.
   */
  currency?: string | null;
  sold?: boolean;
  consigned?: boolean;
}

/**
 * Enough of an artist to introduce them beside their work, and no more.
 *
 * Sized for the SAME modal the piece is shown in — a portrait, a name, a line, a paragraph and
 * where they are. Anything longer belongs on their website, which is what the site itself is for.
 */
export interface ArtistSummary {
  name: string;
  /** One line, from the site's cover headline. */
  headline: string;
  /** A trimmed paragraph of the artist's own words, plain text. */
  about: string;
  /** Portrait, logo, or their hero image — whichever the site offers first. */
  image: string;
  location: string;
  /** Their published site, for a visitor who wants the whole thing. */
  slug: string;
  /** Where an enquiry falls back to when sending fails. */
  contactEmail: string;
}

/** What the visitor typed. */
export interface EnquiryDraft {
  name: string;
  email: string;
  message: string;
}

export interface EnquiryOutcome {
  ok: boolean;
  /** User-facing reason when `ok` is false. Never a raw exception. */
  error: string;
}

/**
 * Sending an enquiry, and saying what happened when it fails.
 *
 * <b>Why this is a service and not three copies.</b> The published artist site raises an enquiry
 * from three places — an inline contact form, an artwork detail panel, and the enquiry modal that
 * panel opens — and the promotional mosaic will be a fourth. Each needs the same request shape, the
 * same "HTTP 200 is not success" handling, and the same mailto fallback. Written per-caller, those
 * drift: one forgets the product reference, another stops checking `Success`, and the artist quietly
 * receives enquiries that do not say which piece they are about.
 *
 * The FORM MARKUP is deliberately not here. The site's inline form is styled by the artist's chosen
 * look through ancestor selectors, which does not survive being moved into an encapsulated
 * component. Logic is shared; presentation stays where its styling works.
 */
@Injectable({ providedIn: 'root' })
export class ArtworkEnquiryService {

  constructor(private artistWebsiteService: ArtistWebsiteService) {}

  /**
   * Sends the enquiry.
   *
   * Resolves to an outcome rather than throwing, because every caller needs to keep the visitor's
   * typed message on screen and offer the fallback — none of them wants an error path.
   */
  public send(identifier: string, draft: EnquiryDraft, work: EnquiryArtwork | null): Observable<EnquiryOutcome> {
    return this.artistWebsiteService.submitEnquiry({
      Identifier: identifier,
      // Naming the piece is what makes an enquiry actionable; null when it came from a contact
      // form rather than a work.
      ArtistProductId: work ? work.id : null,
      SourcePath: this.pagePath(),
      SenderName: draft.name,
      SenderEmail: draft.email,
      Message: draft.message,
    }).pipe(
      map((res: any) => {
        // 200 is not success on this API — the outcome is in `Success`, and the rate limiter
        // answers the same way. A caller trusting the status shows "thank you" for a message that
        // was never delivered.
        if (!res || !res.Success) {
          return {
            ok: false,
            error: (res && res.ResponseMessage) || 'Your message could not be sent. Please try again shortly.',
          };
        }
        return { ok: true, error: '' };
      }),
      catchError(() => of({
        ok: false,
        error: 'Your message could not be sent. Check your connection and try again.',
      })),
    );
  }

  /**
   * The artist behind a piece, summarised from their published site.
   *
   * <b>Read from what the artist wrote, not from a profile table.</b> The published site is the one
   * place an artist has already said who they are in their own words, and it is readable
   * anonymously — which matters, because every visitor to a promotional wall is a stranger.
   *
   * Resolves to `null` rather than erroring when there is no published site. That is a normal
   * answer, not a fault: an artist can consent to discovery without ever building a site, and the
   * caller's job is then to offer nothing rather than an empty panel.
   *
   * Cached per identifier for the life of the page — a visitor clicking six pieces by the same
   * artist should cost one request, not six.
   */
  public artistSummary(identifier: string): Observable<ArtistSummary | null> {
    if (!identifier) { return of(null); }

    const cached = this.summaries.get(identifier);
    if (cached) { return of(cached); }

    return this.artistWebsiteService.getPublished(identifier).pipe(
      map((res: any) => {
        if (!res || !res.Success || !res.ResponseData) { return null; }
        const summary = this.toSummary(res.ResponseData);
        if (summary) { this.summaries.set(identifier, summary); }
        return summary;
      }),
      catchError(() => of(null)),
    );
  }

  private readonly summaries = new Map<string, ArtistSummary>();

  /**
   * Seed copy the website builder writes into a NEW site.
   *
   * Kept because an artist who has not edited a section still has these words stored, and showing
   * them to the public prints the instructions rather than the story — "Tell a little more of your
   * story" under a photograph reads as a broken page. Source: `website-builder.component.ts`.
   */
  private static readonly SEED_TEXT: string[] = [
    'Original work — add a line that sums up what you make.',
    'Tell a little more of your story — your process, a series, a show.',
    'Tell people about your work, your process and your background.',
    'Add your text here — a statement, a story, some news.',
    'A line or two of welcome — what you make, what makes it yours, and why someone should bring it home.',
    'New heading',
    'Your name',
  ];

  private static readonly ABOUT_MAX = 420;

  private toSummary(site: any): ArtistSummary | null {
    const content = site.SiteContent;
    if (!content) { return null; }

    const all: Section[] = (Array.isArray(content.sections) ? content.sections : []).filter((s: any) => !!s);

    // What the artist has hidden is not shown here either — a summary that printed a subhead they
    // switched off would publish exactly what they chose not to.
    const sections = all.filter((s) => !s.hidden);
    const shown = (sec: Section | undefined, key: string): any =>
      sec && !sec.hidden && !isElementHidden(sec, key) ? sec.data?.[key] : undefined;
    const ofType = (type: string) => sections.filter((s) => s.type === type);

    const cover = all.find((s) => s.type === 'cover');
    const contact = all.find((s) => s.type === 'contact');

    // Prefer the artist's own prose over anything generated: a text section they wrote, then an
    // imageText body, then the cover subhead — taking the first that is not still seed copy.
    const candidates: string[] = [
      ...ofType('text').map((s) => shown(s, 'body')),
      ...ofType('imageText').map((s) => shown(s, 'body')),
      shown(cover, 'subhead'),
    ];

    const image = this.usable(shown(cover, 'heroImage'))
      || ofType('imageText').map((s) => shown(s, 'image')).find((i) => this.usable(i))
      || '';

    return {
      name: this.clean(content.siteTitle) || '',
      headline: this.clean(shown(cover, 'headline')) || '',
      about: this.trimTo(candidates.map((c) => this.clean(c)).find((c) => !!c) || '', ArtworkEnquiryService.ABOUT_MAX),
      image: image || '',
      location: this.clean(contact && !contact.hidden ? contact.data?.location : '') || '',
      slug: site.Slug || '',
      // Read even when the contact section is hidden: hiding the block hides the block, it does not
      // switch enquiries off. That is the same rule the published site follows.
      contactEmail: contact?.data?.email || '',
    };
  }

  private usable(url: any): string {
    return typeof url === 'string' && url.trim() ? url : '';
  }

  /** Strip the markup the builder stores, collapse whitespace, and reject unedited seed copy. */
  private clean(html: any): string {
    if (typeof html !== 'string') { return ''; }
    const text = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();
    return ArtworkEnquiryService.SEED_TEXT.includes(text) ? '' : text;
  }

  /** Cut on a word boundary — a summary that stops mid-word reads as truncated data, not an excerpt. */
  private trimTo(text: string, max: number): string {
    if (text.length <= max) { return text; }
    const cut = text.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
  }

  /** True when the visitor has typed enough for the request to be worth making. */
  public isSendable(draft: EnquiryDraft): boolean {
    return !!(draft && draft.message.trim() && draft.email.trim());
  }

  public statusOf(work: EnquiryArtwork): string {
    return work.sold ? 'Sold' : (work.consigned ? 'On consignment' : 'Available');
  }

  /**
   * The direct-mail route, offered only when sending fails.
   *
   * A visitor who has just typed a message should not lose it to a failed request, and the artist's
   * address is on the page anyway. The body carries the piece's REFERENCE as well as its title:
   * titles repeat and get renamed, the id does not, and it is what the artist's own inventory is
   * keyed on.
   */
  public mailto(
    toEmail: string | null | undefined,
    subjectContext: string,
    draft: EnquiryDraft,
    work: EnquiryArtwork | null,
    currency = 'USD',
  ): string {
    if (!toEmail) { return ''; }

    const about = work ? ` about “${work.name}”` : '';
    const subject = `Enquiry${about} — ${subjectContext}`;

    const lines: string[] = [draft.message, ''];

    if (work) {
      lines.push('— — — — — — — — — —', 'PIECE');
      lines.push(`Title:      ${work.name}`);
      lines.push(`Reference:  ${work.id}`);
      if (work.series) { lines.push(`Series:     ${work.series}`); }
      if (work.medium) { lines.push(`Medium:     ${work.medium}`); }
      if (work.dims) { lines.push(`Dimensions: ${work.dims}`); }
      if (work.price) { lines.push(`Price shown: ${currency} ${work.price}`); }
      lines.push(`Status:     ${this.statusOf(work)}`);
      lines.push('');
    }

    lines.push('— — — — — — — — — —', 'ENQUIRY');
    lines.push(`From:       ${draft.name}`);
    lines.push(`Email:      ${draft.email}`);
    lines.push(`Sent:       ${this.sentStamp()}`);
    lines.push(`Page:       ${this.pagePath()}`);

    return `mailto:${toEmail}?subject=${encodeURIComponent(subject)}`
         + `&body=${encodeURIComponent(lines.join('\n'))}`;
  }

  /**
   * When the enquiry was sent, in the VISITOR's local time and stated as such.
   *
   * A mail can sit unread for days and the artist may be in another timezone, so "10:42" alone is
   * ambiguous. The date and offset make it a usable record of when someone asked.
   */
  private sentStamp(): string {
    const d = new Date();
    const offsetMin = -d.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '−';
    const abs = Math.abs(offsetMin);
    const offset = `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
    return `${d.toLocaleString()} (${offset})`;
  }

  /** The page the enquiry came from, so it can be traced back to where it was raised. */
  private pagePath(): string {
    return typeof location !== 'undefined' ? location.pathname : '';
  }
}
